import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { Button } from '../ui/button';
import { LogOut, Menu, Settings, LayoutDashboard, Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllRead, clearNotification, clearAll } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Hide React Navbar on the static landing page redirect (root path)
  if (location.pathname === '/') {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardRoute = () => {
    switch (user?.role) {
      case 'oem':
        return '/oem/dashboard';
      case 'installer':
      case 'junioraligner':
        return '/installer/jobs';
      case 'admin':
        return '/admin/dashboard';
      case 'supervisor':
        return '/supervisor/dashboard';
      default:
        return '/';
    }
  };

  return (
    <nav className="bg-brand-ink border-b border-black/40 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to={isAuthenticated ? getDashboardRoute() : "/"} className="flex items-center space-x-3">
            <img src="/logo.png" alt="Alignworx" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-heading font-extrabold tracking-tight leading-none">
              <span className="text-white">Align</span><span className="text-brand-amber">worx</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to={isAuthenticated ? getDashboardRoute() : "/"} className="text-white/85 hover:text-brand-amber transition-colors">
              Home
            </Link>
            {!isAuthenticated && (
              <>
                <Link to="/#how-it-works" className="text-white/85 hover:text-brand-amber transition-colors">
                  How it Works
                </Link>
                <Link to="/#for-oems" className="text-white/85 hover:text-brand-amber transition-colors">
                  For OEMs
                </Link>
                <Link to="/#for-homeowners" className="text-white/85 hover:text-brand-amber transition-colors">
                  For Homeowners
                </Link>
              </>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-2">
                {/* Notification Bell */}
                <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/10 hover:text-brand-amber">
                      <Bell className="h-5 w-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                      <span className="font-semibold text-sm">Notifications</span>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAllRead(); }}
                            className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"
                          >
                            <CheckCheck className="w-3 h-3" /> Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); clearAll(); }}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-3 py-6 text-center text-gray-400 text-sm">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 20).map(n => (
                        <DropdownMenuItem
                          key={n.id}
                          className={`flex flex-col items-start px-3 py-2 cursor-pointer ${!n.isRead ? 'bg-teal-50' : ''}`}
                          onClick={() => {
                            if (!n.isRead) markAsRead(n.id);
                            if (n.entityType === 'project' && n.entityId) {
                              navigate(`/projects/${n.entityId}/view`);
                              setNotifOpen(false);
                            }
                          }}
                        >
                          <div className="flex items-start gap-2 w-full">
                            {!n.isRead && <span className="w-2 h-2 bg-teal-500 rounded-full mt-1.5 flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm ${!n.isRead ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                                {n.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                              </p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); clearNotification(n.id); }}
                              className="text-gray-300 hover:text-red-500 flex-shrink-0 p-0.5"
                              title="Clear notification"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2 text-white hover:bg-white/10 hover:text-brand-amber">
                      <div className="w-8 h-8 bg-brand-amber rounded-full flex items-center justify-center">
                        <span className="text-brand-ink font-semibold text-sm">
                          {user.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <span className="text-white">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate(getDashboardRoute())}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#how-it-works')}>
                      How it Works
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-oems')}>
                      For OEMs
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-homeowners')}>
                      For Homeowners
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button onClick={() => navigate('/login')} className="bg-brand-amber text-brand-ink hover:bg-brand-amber/90 font-medium">
                Login
              </Button>
            )}
          </div>

          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-brand-amber">
                  <Menu className="h-6 w-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate(isAuthenticated ? getDashboardRoute() : '/')}>Home</DropdownMenuItem>
                {!isAuthenticated && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/#how-it-works')}>
                      How it Works
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-oems')}>
                      For OEMs
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-homeowners')}>
                      For Homeowners
                    </DropdownMenuItem>
                  </>
                )}
                {isAuthenticated ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate(getDashboardRoute())}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#how-it-works')}>
                      How it Works
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-oems')}>
                      For OEMs
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/#for-homeowners')}>
                      For Homeowners
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={() => navigate('/login')}>Login</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;